import React, { useState, useEffect, useCallback, useRef } from 'react';
import { empresaAPI, getEmpresaPublicInfo } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Building2,
  Upload,
  Trash2,
  Save,
  KeyRound,
  Eye,
  EyeOff,
  CreditCard,
  User,
  Image as ImageIcon,
  CalendarDays,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConfiguracaoEmpresa = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    nome_empresa: '',
    nome_proprietario: '',
    tem_logo: false,
    exibir_numero_projeto: true,
    chat_enabled: false,
    ocultar_propostas: false,
    plano: '',
    status_assinatura: '',
    proximo_vencimento: '',
    admin_nome: '',
    admin_email: '',
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const fileInputRef = useRef(null);

  // Dialog de alterar senha
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [credForm, setCredForm] = useState({
    senha_atual: '',
    novo_nome: '',
    novo_email: '',
    nova_senha: '',
    confirmar_senha: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    atual: false,
    nova: false,
    confirmar: false,
  });
  const [savingCred, setSavingCred] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await empresaAPI.getConfig();
      setConfig(res.data);
      if (res.data.tem_logo) {
        setLogoPreview(`${API_URL}/api/empresa/logo?t=${Date.now()}`);
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações da empresa');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use: PNG, JPG, SVG ou WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 2MB');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    try {
      setSaving(true);
      await empresaAPI.uploadLogo(logoFile);
      toast.success('Logo atualizado com sucesso');
      setLogoFile(null);
      setLogoPreview(`${API_URL}/api/empresa/logo?t=${Date.now()}`);
      setConfig(prev => ({ ...prev, tem_logo: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar logo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Remover o logo da empresa?')) return;
    try {
      await empresaAPI.deleteLogo();
      toast.success('Logo removido');
      setLogoPreview(null);
      setLogoFile(null);
      setConfig(prev => ({ ...prev, tem_logo: false }));
    } catch (error) {
      toast.error('Erro ao remover logo');
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await empresaAPI.updateConfig({
        nome_empresa: config.nome_empresa,
        nome_proprietario: config.nome_proprietario,
        exibir_numero_projeto: config.exibir_numero_projeto,
        chat_enabled: config.chat_enabled,
        ocultar_propostas: !!config.ocultar_propostas,
      });
      getEmpresaPublicInfo(true);  // atualiza o menu na próxima navegação
      toast.success('Configurações salvas com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!credForm.senha_atual) {
      toast.error('Informe a senha atual');
      return;
    }
    if (credForm.nova_senha && credForm.nova_senha !== credForm.confirmar_senha) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (credForm.nova_senha && credForm.nova_senha.length < 6) {
      toast.error('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }
    if (!credForm.novo_nome && !credForm.novo_email && !credForm.nova_senha) {
      toast.error('Altere pelo menos um campo');
      return;
    }

    try {
      setSavingCred(true);
      await empresaAPI.updateAdminCredentials({
        senha_atual: credForm.senha_atual,
        novo_nome: credForm.novo_nome || undefined,
        novo_email: credForm.novo_email || undefined,
        nova_senha: credForm.nova_senha || undefined,
      });
      toast.success('Credenciais atualizadas com sucesso');
      setShowCredentialsDialog(false);
      setCredForm({ senha_atual: '', novo_nome: '', novo_email: '', nova_senha: '', confirmar_senha: '' });
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar credenciais');
    } finally {
      setSavingCred(false);
    }
  };

  const formatPlanName = (plan) => {
    if (!plan) return 'Não definido';
    const names = {
      monthly: 'Mensal',
      yearly: 'Anual',
      lifetime: 'Vitalício',
      trial: 'Período de Teste',
    };
    return names[plan] || plan;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Não definido';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const statusColor = (status) => {
    const colors = {
      active: 'default',
      trial: 'outline',
      past_due: 'destructive',
      suspended: 'destructive',
      canceled: 'secondary',
    };
    return colors[status] || 'outline';
  };

  const statusLabel = (status) => {
    const labels = {
      active: 'Ativo',
      trial: 'Teste',
      past_due: 'Pagamento Pendente',
      suspended: 'Suspenso',
      canceled: 'Cancelado',
    };
    return labels[status] || status || 'Não definido';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-80" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="config-empresa-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Configurações da Empresa</h1>
        <p className="text-muted-foreground">Gerencie o logo, dados da empresa e credenciais do administrador</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Logo Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Logo da Empresa
            </CardTitle>
            <CardDescription>
              O logo aparece na tela de login e no menu lateral. Formatos: PNG, JPG, SVG, WebP (máx 2MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center gap-4">
              <div
                className="w-40 h-40 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/30 cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="logo-preview-area"
              >
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo da empresa"
                    className="w-full h-full object-contain p-2"
                    data-testid="logo-preview-img"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <span className="text-sm">Clique para selecionar</span>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                className="hidden"
                onChange={handleLogoSelect}
                data-testid="logo-file-input"
              />

              <div className="flex gap-2">
                {logoFile && (
                  <Button onClick={handleUploadLogo} disabled={saving} data-testid="logo-upload-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    {saving ? 'Enviando...' : 'Salvar Logo'}
                  </Button>
                )}
                {config.tem_logo && !logoFile && (
                  <Button variant="outline" onClick={handleDeleteLogo} data-testid="logo-delete-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remover Logo
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dados da Empresa */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Dados da Empresa
            </CardTitle>
            <CardDescription>Informações gerais sobre a empresa</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome_empresa">Nome da Empresa</Label>
              <Input
                id="nome_empresa"
                value={config.nome_empresa}
                onChange={(e) => setConfig(prev => ({ ...prev, nome_empresa: e.target.value }))}
                placeholder="Ex: AgroLink Consultoria"
                data-testid="input-nome-empresa"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nome_proprietario">Nome Completo do Proprietário</Label>
              <Input
                id="nome_proprietario"
                value={config.nome_proprietario}
                onChange={(e) => setConfig(prev => ({ ...prev, nome_proprietario: e.target.value }))}
                placeholder="Ex: João da Silva"
                data-testid="input-nome-proprietario"
              />
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="exibir_numero_projeto" className="font-medium">
                  Exibir numerador de projetos
                </Label>
                <p className="text-xs text-muted-foreground">
                  Controla se o número interno (ex: <span className="font-mono">26-00001</span>) aparece antes do nome do cliente nas listagens.
                  Desativar não interrompe a numeração — projetos continuam sendo numerados internamente.
                </p>
              </div>
              <Switch
                id="exibir_numero_projeto"
                checked={config.exibir_numero_projeto}
                onCheckedChange={(v) => setConfig(prev => ({ ...prev, exibir_numero_projeto: v }))}
                data-testid="switch-exibir-numero"
              />
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="chat_enabled" className="font-medium">
                  Habilitar Chat interno
                </Label>
                <p className="text-xs text-muted-foreground">
                  Chat 1-para-1 em tempo real entre usuários da empresa (com status online / ausente / ocupado).
                  Aparece como uma bolha flutuante no canto inferior direito de todas as telas.
                </p>
              </div>
              <Switch
                id="chat_enabled"
                checked={!!config.chat_enabled}
                onCheckedChange={(v) => setConfig(prev => ({ ...prev, chat_enabled: v }))}
                data-testid="switch-chat-enabled"
              />
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="ocultar_propostas" className="font-medium">
                  Ocultar página Propostas
                </Label>
                <p className="text-xs text-muted-foreground">
                  Esconde "Propostas" do menu e o aviso de propostas em aberto, para todos os usuários.
                  Nenhuma proposta é apagada: ao desativar, tudo volta a aparecer.
                </p>
              </div>
              <Switch
                id="ocultar_propostas"
                checked={!!config.ocultar_propostas}
                onCheckedChange={(v) => setConfig(prev => ({ ...prev, ocultar_propostas: v }))}
                data-testid="switch-ocultar-propostas"
              />
            </div>

            <Button onClick={handleSaveConfig} disabled={saving} className="w-full" data-testid="save-empresa-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Dados'}
            </Button>
          </CardContent>
        </Card>

        {/* Administrador Master */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Administrador Master
            </CardTitle>
            <CardDescription>Credenciais de acesso do administrador principal</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">Nome</Label>
              <p className="font-medium" data-testid="admin-nome">{config.admin_nome || '-'}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">E-mail</Label>
              <p className="font-medium" data-testid="admin-email">{config.admin_email || '-'}</p>
            </div>
            <Separator />
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setCredForm({
                  senha_atual: '',
                  novo_nome: config.admin_nome || '',
                  novo_email: config.admin_email || '',
                  nova_senha: '',
                  confirmar_senha: '',
                });
                setShowCredentialsDialog(true);
              }}
              data-testid="change-credentials-btn"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Alterar Usuário e Senha
            </Button>
          </CardContent>
        </Card>

        {/* Plano / Assinatura */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Plano e Assinatura
            </CardTitle>
            <CardDescription>Informações sobre o plano contratado</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-muted-foreground text-xs uppercase tracking-wider">Plano Atual</Label>
                <p className="font-medium text-lg" data-testid="plan-name">{formatPlanName(config.plano)}</p>
              </div>
              <Badge variant={statusColor(config.status_assinatura)} data-testid="plan-status">
                {statusLabel(config.status_assinatura)}
              </Badge>
            </div>
            <Separator />
            <div className="flex items-center gap-3">
              <CalendarDays className="w-5 h-5 text-muted-foreground" />
              <div>
                <Label className="text-muted-foreground text-xs uppercase tracking-wider">Próximo Vencimento</Label>
                <p className="font-medium" data-testid="plan-due-date">{formatDate(config.proximo_vencimento)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Credenciais */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Alterar Credenciais do Administrador
            </DialogTitle>
            <DialogDescription>
              Informe a senha atual para confirmar a alteração
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Senha Atual *</Label>
              <div className="relative">
                <Input
                  type={showPasswords.atual ? 'text' : 'password'}
                  value={credForm.senha_atual}
                  onChange={(e) => setCredForm(prev => ({ ...prev, senha_atual: e.target.value }))}
                  placeholder="Digite a senha atual"
                  className="pr-10"
                  data-testid="input-senha-atual"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full w-10 hover:bg-transparent"
                  onClick={() => setShowPasswords(p => ({ ...p, atual: !p.atual }))}
                >
                  {showPasswords.atual ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>Novo Nome</Label>
              <Input
                value={credForm.novo_nome}
                onChange={(e) => setCredForm(prev => ({ ...prev, novo_nome: e.target.value }))}
                placeholder="Nome do administrador"
                data-testid="input-novo-nome"
              />
            </div>
            <div className="space-y-2">
              <Label>Novo E-mail</Label>
              <Input
                type="email"
                value={credForm.novo_email}
                onChange={(e) => setCredForm(prev => ({ ...prev, novo_email: e.target.value }))}
                placeholder="email@empresa.com"
                data-testid="input-novo-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Nova Senha (deixe em branco para manter)</Label>
              <div className="relative">
                <Input
                  type={showPasswords.nova ? 'text' : 'password'}
                  value={credForm.nova_senha}
                  onChange={(e) => setCredForm(prev => ({ ...prev, nova_senha: e.target.value }))}
                  placeholder="Nova senha (mín. 6 caracteres)"
                  className="pr-10"
                  data-testid="input-nova-senha"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full w-10 hover:bg-transparent"
                  onClick={() => setShowPasswords(p => ({ ...p, nova: !p.nova }))}
                >
                  {showPasswords.nova ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            {credForm.nova_senha && (
              <div className="space-y-2">
                <Label>Confirmar Nova Senha</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirmar ? 'text' : 'password'}
                    value={credForm.confirmar_senha}
                    onChange={(e) => setCredForm(prev => ({ ...prev, confirmar_senha: e.target.value }))}
                    placeholder="Repita a nova senha"
                    className="pr-10"
                    data-testid="input-confirmar-senha"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full w-10 hover:bg-transparent"
                    onClick={() => setShowPasswords(p => ({ ...p, confirmar: !p.confirmar }))}
                  >
                    {showPasswords.confirmar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {credForm.nova_senha && credForm.confirmar_senha && credForm.nova_senha !== credForm.confirmar_senha && (
                  <p className="text-sm text-destructive">As senhas não coincidem</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveCredentials} disabled={savingCred} data-testid="save-credentials-btn">
              <Save className="w-4 h-4 mr-2" />
              {savingCred ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracaoEmpresa;
